import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { formatPrice } from 'common/formatters';

import SrBox from 'client/components/SrBox';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_HEIGHT_TALL,
  SR_BUTTON_FONT_SIZE_SMALL,
} from 'client/components/SrButton/SrButton';
import SrCMADiscussionModal from 'client/components/SrCMADiscussionModal/SrCMADiscussionModal';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrCMAHistoryDataTable from 'client/components/SrCMAHistoryDataTable/SrCMAHistoryDataTable';
import SrLightbox from 'client/components/SrLightbox';

import useSrPriceComparisonCardController from 'client/components/SrPriceComparisonCard/useSrPriceComparisonCardController';

import './SrPriceComparisonCard.less';

const SrPriceComparisonCard = ({ listing, isAdmin, userAgent, appUrl, openLowerPriceModal }) => {
  const controller = useSrPriceComparisonCardController({
    listing,
    userAgent,
    appUrl,
    openLowerPriceModal,
  });

  const { pricing, notes, cma, cmaDiscussionModal, zillowMap, actions } = controller;

  return (
    <>
      <SrBox boxSizing="border-box" borderRadius="14px" styleName={controller.pricing.color}>
        <SrFlex justify="between" width="calc(100% - 32px)" margin="0 auto">
          <SrBox styleName="priceContainer">
            <SrText
              display="block"
              font="greycliff"
              weight="800"
              size="28"
              align="center"
              lineHeight="34px"
              color="greenDark"
            >
              {formatPrice(pricing.suggested)}
            </SrText>
            <SrBox margin="4px 0px">
              <SrText
                display="block"
                align="center"
                fontSize="14px"
                lineHeight="16px"
                weight="800"
                font="greycliff"
                color="greenDark"
              >
                {pricing.suggestedLabel}
              </SrText>
            </SrBox>
            <SrBox>
              <SrText
                font="greycliff"
                weight="800"
                color="blue300"
                fontSize="14px"
                lineHeight="19px"
                align="center"
                display="block"
                cursor="pointer"
                onClick={actions.viewReport.onClick}
              >
                {actions.viewReport.label}
              </SrText>
            </SrBox>
          </SrBox>
          <SrBox styleName={classNames('priceContainer', 'currentPriceContainer')}>
            {pricing.isTooHigh ? (
              <>
                <SrBox>
                  <SrText
                    styleName="priceLeft"
                    display="block"
                    font="greycliff"
                    weight="800"
                    size="28"
                    align="center"
                    lineHeight="34px"
                  >
                    {formatPrice(pricing.overRateDiff)}
                  </SrText>
                </SrBox>
                <SrBox margin="4px 0px">
                  <SrText
                    styleName="currentPriceLabel"
                    display="block"
                    align="center"
                    size="14"
                    lineHeight="16px"
                    weight="800"
                    font="greycliff"
                    color="greenDark"
                  >
                    {pricing.overRateLabel}
                  </SrText>
                </SrBox>
                <SrBox>
                  <SrText
                    styleName="currentPriceLabel"
                    display="block"
                    align="center"
                    size="14"
                    lineHeight="16px"
                    weight="800"
                    font="greycliff"
                    color="greenDark"
                  >
                    {pricing.currentPriceText}
                  </SrText>
                </SrBox>
              </>
            ) : (
              <>
                <SrBox>
                  <SrText
                    styleName="priceLeft"
                    display="block"
                    font="greycliff"
                    weight="800"
                    size="28"
                    align="center"
                    lineHeight="34px"
                  >
                    {formatPrice(pricing.listing)}
                  </SrText>
                </SrBox>
                <SrBox>
                  <SrText
                    styleName="currentPriceLabel"
                    display="block"
                    align="center"
                    size="14"
                    lineHeight="16px"
                    weight="800"
                    font="greycliff"
                    color="greenDark"
                  >
                    {pricing.listingLabel}
                  </SrText>
                </SrBox>
              </>
            )}
          </SrBox>
        </SrFlex>
        {isAdmin && (
          <SrFlex styleName={classNames('cmaDiscussionContainer', cma.discussion.header.color)}>
            <SrFlex align="center" justify="center" paddingLeft="10px" paddingRight="10px">
              <SrBox
                width="20px"
                height="20px"
                backgroundImage={`url(${cma.discussion.header.icon})`}
                marginRight="8px"
              />
              <SrBox styleName="cmaDiscussionText">
                <SrText font="greycliff" weight="800" color={cma.discussion.header.color}>
                  {cma.discussion.header.text}
                </SrText>
              </SrBox>
            </SrFlex>
            {notes.shouldShowNotes && (
              <SrFlex
                align="center"
                justify="center"
                marginTop="16px"
                paddingLeft="10px"
                paddingRight="10px"
              >
                <SrBox maxWidth="556px" textAlign="center">
                  <SrText
                    font="greycliff"
                    color="grey"
                    weight="600"
                    size="18px"
                    lineHeight="24px"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                  >
                    {cma.discussion.notes}
                  </SrText>
                </SrBox>
              </SrFlex>
            )}
            {controller.isRentPriceTooHigh && (
              <SrFlex
                marginTop="14px"
                align="center"
                justify="center"
                cursor="pointer"
                onClick={cmaDiscussionModal.onOpen}
              >
                <SrText font="greycliff" weight="800" color="blue300">
                  Update pricing conversation &gt;
                </SrText>
              </SrFlex>
            )}
          </SrFlex>
        )}
        {notes.hasNotes && (
          <SrBox width="calc(100% - 32px)" margin="16px auto">
            <SrText
              display="block"
              font="greycliff"
              color="blueGreycliff"
              weight="800"
              fontSize="16px"
              lineHeight="25px"
              align="center"
            >
              {controller.notes.title}
            </SrText>
            <SrBox margin="7px auto 0">
              <SrText
                display="block"
                font="greycliff"
                color="blueGreycliff"
                weight="600"
                fontSize="16px"
                lineHeight="24px"
                whiteSpace="break-spaces"
                align="center"
              >
                {controller.notes.text}
              </SrText>
            </SrBox>
          </SrBox>
        )}
        {actions.lowerPrice.label && actions.lowerPrice.onClick && (
          <SrBox margin="24px auto 21px" maxWidth="303px">
            <SrButton
              theme={SR_BUTTON_THEME_ADOBE_BLUE}
              heightType={SR_BUTTON_HEIGHT_TALL}
              fontSizeType={SR_BUTTON_FONT_SIZE_SMALL}
              onClick={actions.lowerPrice.onClick}
            >
              {actions.lowerPrice.label}
            </SrButton>
          </SrBox>
        )}
        {cma.nearbyZillowListings.hasInformation && (
          <SrFlex width="100%" marginTop="34px" direction="col">
            <SrBox
              height="32px"
              width="140px"
              backgroundColor="white"
              borderRadius="2px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              top="88px"
              left="calc(100% - 168px)"
              padding="2px"
              zIndex="2"
              cursor="pointer"
              onClick={zillowMap.onZillowRedirect}
            >
              <SrText
                display="block"
                font="greycliff"
                color="blue300"
                weight="700"
                fontSize="18px"
                lineHeight="25px"
              >
                View Map
              </SrText>
              <SrBox
                width="20px"
                height="20px"
                marginLeft="8px"
                backgroundImage="url(/images/arrow-external-link.svg)"
                backgroundSize="contain"
                backgroundRepeat="no-repeat"
                backgroundPosition="center"
              />
            </SrBox>
            <SrBox marginLeft="23px">
              <SrText
                display="block"
                font="greycliff"
                color="purpleDark"
                weight="700"
                fontSize="18px"
                lineHeight="25px"
              >
                {cma.nearbyZillowListings.title}
              </SrText>
            </SrBox>
            <SrBox
              width="calc(100% - 32px)"
              margin="15px auto"
              height="384px"
              borderRadius="18px"
              overflow="hidden"
              cursor="pointer"
              onClick={zillowMap.onExpandMap}
              backgroundImage={`url(${cma.nearbyZillowListings.screenshotUrl})`}
              backgroundSize="cover"
              backgroundPosition="center"
            />
          </SrFlex>
        )}
        <SrBox width="calc(100% - 32px)" margin="24px auto">
          <SrBox marginBottom="12px">
            <SrText font="greycliff" weight="800" fontSize="18px" lineHeight="24px">
              CMA History
            </SrText>
          </SrBox>
          <SrCMAHistoryDataTable listingId={listing.id} />
        </SrBox>
      </SrBox>
      {cmaDiscussionModal.isOpen && (
        <SrCMADiscussionModal
          listingId={listing.id}
          onClose={cmaDiscussionModal.onClose}
          onSuccess={cmaDiscussionModal.onSuccess}
          cmaDiscussed={cma.discussion.hasBeenDiscussed}
          cmaDiscussionNotes={cma.discussion.notes}
        />
      )}
      {zillowMap.isMapExpanded && (
        <SrLightbox
          photoUrls={[cma.nearbyZillowListings.screenshotUrl]}
          currentImage={0}
          isOpen={zillowMap.isMapExpanded}
          onClickPrev={() => {}}
          onClickNext={() => {}}
          onClose={zillowMap.onClose}
        />
      )}
    </>
  );
};

SrPriceComparisonCard.propTypes = {
  listing: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  userAgent: PropTypes.object.isRequired,
  appUrl: PropTypes.string.isRequired,
};

SrPriceComparisonCard.displayName = 'SrPriceComparisonCard';

export default SrPriceComparisonCard;
